/**
 * MembershipAgreementsAdmin.tsx — Admin > Membership Agreements.
 *
 * The corporate dashboard's Membership Agreements page for the signed-in
 * account, in the Lead Sources layout (see components/admin/AdminList.tsx):
 *   - one row per agreement (its latest version): Title and Version, by
 *     title; search by title; Show deleted;
 *   - "Add agreement" top right; a click on a live row opens it; Edit,
 *     Version history and Delete on a live row, Restore on a deleted one;
 *   - corp's form: Title * and Agreement Body * (rich text, with Insert
 *     Shortcode). Saving an edit makes a new version and ends the current
 *     one, so Save is off until something changes;
 *   - corp's Version History: every version, the current one marked, each
 *     previewed with its dates.
 * On a Locked page (useAdminPageLevel) there is no Add, Edit, Delete or
 * Restore; a live row still opens, read-only (no Insert Shortcode), and
 * Version history, which only reads, stays.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Braces, History, Plus } from 'lucide-react';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { SpinnerOverlay } from '@/components/ui/Spinner';
import {
  ACTIONS_COLUMN,
  ADMIN_TD_CLASS,
  AdminListEmpty,
  AdminListPage,
  AdminListTable,
  AdminListToolbar,
  AdminRowName,
  PaginationFooter,
  adminRowClass,
  type AdminListColumn,
} from '@/components/admin/AdminList';
import { AdminFormModal } from '@/components/admin/AdminFormModal';
import { useAdminPageLevel } from '@/components/admin/AdminPageLevelContext';
import { AdminRowAction, AdminRowActions, ROW_ACTION_ICON_CLASS, RowActionButton } from '@/components/admin/AdminRowActions';
import { DialogField, fieldClasses } from '@/components/admin/DialogChrome';
import { OutlineBadge } from '@/components/admin/CorpBadges';
import { ShortcodeDialog } from '@/components/admin/ShortcodeDialog';
import { useListPagination } from '@/components/admin/useListPagination';
import { compareText, useListSort } from '@/components/admin/useListSort';
import { useTrackedForm } from '@/components/admin/useTrackedForm';
import { useFieldLocked } from '@/components/ui/FieldLock';
import { cn } from '@/lib/utils';
import { getErrorMessage } from '@/services/apiClient';
import {
  createAgreement,
  deleteAgreement,
  groupAgreements,
  listAgreementVersions,
  restoreAgreement,
  saveAgreementVersion,
  type AgreementVersion,
} from '@/services/membershipAgreements.service';
import { toast } from '@/store/toastStore';
import { TiptapEditor, type Editor } from '@/v5/components/ui/tiptap-editor';

const COLUMNS: AdminListColumn[] = [
  { key: 'title', header: 'Title', sortable: true },
  { key: 'version', header: 'Version', sortable: true, className: 'w-28' },
  ACTIONS_COLUMN,
];

function agreementTitle(a: AgreementVersion): string {
  return a.title.trim() || `Agreement #${a.groupID}`;
}

/** The body's text, for the required check (the editor leaves "<p></p>" when emptied). */
function bodyText(html: string): string {
  return html.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim();
}

/** "Oct 6, 2026 at 2:05 PM" from a stored "YYYY-MM-DD HH:MM:SS", read as written (no timezone shift). */
export function formatAgreementDate(value: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{2}):(\d{2}))?/.exec(value);
  if (!m || m[1] === '0000') return '—';
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const date = `${months[Number(m[2]) - 1]} ${Number(m[3])}, ${m[1]}`;
  if (m[4] === undefined) return date;
  const h = Number(m[4]);
  return `${date} at ${h % 12 || 12}:${m[5]} ${h < 12 ? 'AM' : 'PM'}`;
}

export function MembershipAgreementsAdmin() {
  const locked = useAdminPageLevel() === 'locked';
  const [rows, setRows] = useState<AgreementVersion[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showDeleted, setShowDeleted] = useState(false);
  const [editing, setEditing] = useState<AgreementVersion | 'new' | null>(null);
  const [deleting, setDeleting] = useState<AgreementVersion | null>(null);
  const [history, setHistory] = useState<AgreementVersion | null>(null);

  const load = useCallback(async (quiet = false) => {
    if (!quiet) setLoading(true);
    try {
      setRows(await listAgreementVersions());
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to load membership agreements'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const { latest, versions } = useMemo(() => groupAgreements(rows), [rows]);

  const query = search.trim().toLowerCase();
  const listRows = useMemo(
    () =>
      latest
        .filter((a) => (showDeleted || a.active) && (query === '' || a.title.toLowerCase().includes(query)))
        .sort((a, b) => compareText(agreementTitle(a), agreementTitle(b)) || a.groupID - b.groupID),
    [latest, showDeleted, query],
  );
  const sorting = useListSort(listRows, {
    initialKey: 'title',
    compare: {
      title: (a, b) => compareText(agreementTitle(a), agreementTitle(b)),
      version: (a, b) => a.version - b.version,
    },
  });
  const pagination = useListPagination(sorting.sorted, { resetOn: [query, showDeleted] });

  async function handleDelete() {
    if (!deleting) return;
    const title = agreementTitle(deleting);
    try {
      await deleteAgreement(deleting.membershipAgreementID);
      toast.success(`"${title}" deleted`);
      void load(true);
    } catch (err) {
      toast.error(getErrorMessage(err, 'The agreement could not be deleted.'));
    }
  }

  async function handleRestore(a: AgreementVersion) {
    try {
      await restoreAgreement(a.membershipAgreementID);
      toast.success('Agreement restored');
      void load(true);
    } catch (err) {
      toast.error(getErrorMessage(err, 'The agreement could not be restored.'));
    }
  }

  return (
    <AdminListPage
      title="Membership Agreements"
      subtitle="Manage membership agreement templates."
      actions={
        <Button leftIcon={<Plus className="h-4 w-4" />} onClick={() => setEditing('new')}>
          Add agreement
        </Button>
      }
      toolbar={
        <AdminListToolbar
          search={search}
          onSearchChange={setSearch}
          searchPlaceholder="Search agreements..."
          searchLabel="Search agreements"
          showDeleted={showDeleted}
          onShowDeletedChange={setShowDeleted}
          showDeletedId="membership-agreements-show-deleted"
        />
      }
    >
      {loading ? (
        <SpinnerOverlay label="Loading membership agreements…" />
      ) : listRows.length === 0 ? (
        <AdminListEmpty
          title={query ? 'No membership agreements found' : 'No membership agreements yet'}
          hint={query ? 'Try adjusting your search.' : locked ? undefined : 'Add an agreement to get started.'}
        />
      ) : (
        <>
          <AdminListTable columns={COLUMNS} aria-label="Membership agreements" sort={sorting.sort} onSortChange={sorting.onSortChange}>
            {pagination.pageItems.map((a) => {
              const deleted = !a.active;
              const title = agreementTitle(a);
              return (
                <tr
                  key={a.groupID}
                  className={adminRowClass({ deleted, clickable: !deleted })}
                  onClick={deleted ? undefined : () => setEditing(a)}
                >
                  <td className={ADMIN_TD_CLASS}>
                    <AdminRowName deleted={deleted} nameTestId="agreement-title">
                      {title}
                    </AdminRowName>
                  </td>
                  <td className={ADMIN_TD_CLASS}>
                    <OutlineBadge>v{a.version}</OutlineBadge>
                  </td>
                  <td className={ADMIN_TD_CLASS}>
                    <AdminRowActions>
                      {deleted ? (
                        <AdminRowAction kind="restore" label={`Restore ${title}`} onClick={() => void handleRestore(a)} />
                      ) : (
                        <>
                          <AdminRowAction kind="edit" label={`Edit ${title}`} onClick={() => setEditing(a)} />
                          <RowActionButton
                            label={`Version history for ${title}`}
                            title="Version history"
                            icon={<History className={ROW_ACTION_ICON_CLASS} />}
                            onClick={() => setHistory(a)}
                          />
                          <AdminRowAction kind="delete" label={`Delete ${title}`} onClick={() => setDeleting(a)} />
                        </>
                      )}
                    </AdminRowActions>
                  </td>
                </tr>
              );
            })}
          </AdminListTable>
          <PaginationFooter pagination={pagination} noun={['agreement', 'agreements']} />
        </>
      )}

      {editing && (
        <AgreementFormModal
          key={editing === 'new' ? 'new' : editing.membershipAgreementID}
          agreement={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            void load(true);
          }}
        />
      )}

      {history && (
        <VersionHistoryModal
          title={agreementTitle(history)}
          versions={versions.get(history.groupID) ?? [history]}
          onClose={() => setHistory(null)}
        />
      )}

      <ConfirmDialog
        isOpen={deleting !== null}
        onClose={() => setDeleting(null)}
        onConfirm={handleDelete}
        title={deleting ? `Delete "${agreementTitle(deleting)}"?` : ''}
        message="Memberships that use it keep their link, and you can restore it from Show deleted."
        confirmLabel="Delete agreement"
        variant="danger"
      />
    </AdminListPage>
  );
}

// ─── Form ──────────────────────────────────────────────────────────────────────

/**
 * The rich-text body, kept in step with the form: Revert pushes the saved
 * HTML back into the editor, which does not follow its `content` prop.
 */
function AgreementBodyEditor({ value, onChange, invalid }: { value: string; onChange: (html: string) => void; invalid: boolean }) {
  const editorRef = useRef<Editor | null>(null);
  const lastEmitted = useRef(value);
  const [shortcodes, setShortcodes] = useState(false);
  // Locked (inside a locked form): the body only shows.
  const locked = useFieldLocked();

  useEffect(() => {
    if (value === lastEmitted.current) return;
    editorRef.current?.commands.setContent(value || '', { emitUpdate: false });
    lastEmitted.current = value;
  }, [value]);

  function emit(html: string) {
    lastEmitted.current = html;
    onChange(html);
  }

  return (
    <div className="space-y-2">
      {!locked && (
        <div className="flex justify-end">
          <Button variant="secondary" size="xs" leftIcon={<Braces className="h-3.5 w-3.5" />} onClick={() => setShortcodes(true)}>
            Insert Shortcode
          </Button>
        </div>
      )}
      <div className={cn('rounded-md border border-slate-200', invalid && fieldClasses.input('error'))} data-testid="agreement-body">
        <TiptapEditor content={value} onChange={emit} placeholder="Enter agreement content..." editorRef={editorRef} />
      </div>
      {shortcodes && (
        <ShortcodeDialog
          onClose={() => setShortcodes(false)}
          onInsert={(code) => {
            const editor = editorRef.current;
            if (editor) {
              editor.chain().focus().insertContent(code).run();
              emit(editor.getHTML());
            }
            setShortcodes(false);
          }}
        />
      )}
    </div>
  );
}

function AgreementFormModal({
  agreement,
  onClose,
  onSaved,
}: {
  agreement: AgreementVersion | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const isNew = agreement === null;
  const locked = useAdminPageLevel() === 'locked';
  const t = useTrackedForm(() => ({ title: agreement?.title ?? '', bodyHtml: agreement?.bodyHtml ?? '' }), { tracking: !isNew });
  const [saving, setSaving] = useState(false);
  const missingTitle = t.form.title.trim() === '';
  const missingBody = bodyText(t.form.bodyHtml) === '';
  const errors = [...(missingTitle ? ['Title is required'] : []), ...(missingBody ? ['Body content is required'] : [])];

  async function save() {
    if (errors.length > 0) return;
    setSaving(true);
    const data = { title: t.form.title.trim(), bodyHtml: t.form.bodyHtml };
    try {
      if (agreement) {
        await saveAgreementVersion(agreement.membershipAgreementID, data);
        toast.success(`"${data.title}" updated (new version created)`);
      } else {
        await createAgreement(data);
        toast.success('Agreement created');
      }
      onSaved();
    } catch (err) {
      toast.error(getErrorMessage(err, 'The agreement could not be saved.'));
    } finally {
      setSaving(false);
    }
  }

  return (
    <AdminFormModal
      title={isNew ? 'Add Agreement' : `Edit Agreement (v${agreement.version})`}
      isNew={isNew}
      dirty={isNew ? t.form.title !== '' || !missingBody : t.dirty}
      saving={saving}
      errors={errors}
      saveLabel={isNew ? 'Create' : 'Save (New Version)'}
      // A save always makes a new version, so an unchanged agreement is not saved.
      canSave={isNew || t.dirty}
      onSave={() => void save()}
      onRevertAll={t.revertAll}
      onClose={onClose}
      size="xl"
    >
      <div className="grid gap-4">
        <DialogField
          label="Title"
          htmlFor="agreement-title"
          required
          state={missingTitle ? 'error' : 'none'}
          labelHint={missingTitle ? '— required' : undefined}
          badge={t.badge('title', 'Title')}
        >
          <Input
            id="agreement-title"
            value={t.form.title}
            placeholder="Agreement title"
            maxLength={255}
            onChange={(e) => t.patch({ title: e.target.value })}
            className={fieldClasses.input(missingTitle ? 'error' : 'none')}
          />
        </DialogField>
        <DialogField
          label="Agreement Body"
          required
          state={missingBody ? 'error' : 'none'}
          labelHint={missingBody ? '— required' : undefined}
          badge={t.badge('bodyHtml', 'Agreement Body')}
        >
          <AgreementBodyEditor value={t.form.bodyHtml} onChange={(bodyHtml) => t.patch({ bodyHtml })} invalid={missingBody} />
        </DialogField>
        {!isNew && !locked && (
          <p className="text-xs text-slate-500">
            Saving will create a new version (v{agreement.version + 1}) and deactivate the current version.
          </p>
        )}
      </div>
    </AdminFormModal>
  );
}

// ─── Version history ───────────────────────────────────────────────────────────

function VersionHistoryModal({ title, versions, onClose }: { title: string; versions: AgreementVersion[]; onClose: () => void }) {
  const [selectedId, setSelectedId] = useState<number | null>(versions[0]?.membershipAgreementID ?? null);
  const selected = versions.find((v) => v.membershipAgreementID === selectedId) ?? null;
  const currentId = versions[0]?.active ? versions[0].membershipAgreementID : null;

  return (
    <Modal
      isOpen
      onClose={onClose}
      size="xl"
      title={
        <div className="flex flex-col gap-2">
          <h2 className="text-lg font-semibold leading-none text-slate-900">Version History — {title}</h2>
          <p className="text-sm text-slate-500">
            {versions.length} version{versions.length === 1 ? '' : 's'}
          </p>
        </div>
      }
      footer={
        <div className="flex w-full justify-end">
          <Button variant="secondary" onClick={onClose}>
            Close
          </Button>
        </div>
      }
    >
      {versions.length === 0 ? (
        <p className="py-8 text-center text-sm text-slate-500">No versions found</p>
      ) : (
        <div className="flex min-h-[50vh] flex-col gap-4 md:flex-row">
          <ul className="space-y-1 md:w-[220px] md:shrink-0" aria-label="Versions">
            {versions.map((v) => (
              <li key={v.membershipAgreementID}>
                <button
                  type="button"
                  aria-pressed={v.membershipAgreementID === selectedId}
                  onClick={() => setSelectedId(v.membershipAgreementID)}
                  className={cn(
                    'w-full rounded-md border px-3 py-2 text-left transition-colors',
                    v.membershipAgreementID === selectedId ? 'border-blue-300 bg-blue-50' : 'border-transparent hover:bg-slate-50',
                  )}
                >
                  <span className="flex items-center gap-2 text-sm font-medium text-slate-900">
                    Version {v.version}
                    {v.membershipAgreementID === currentId ? (
                      <span className="rounded-full border border-green-200 bg-green-100 px-1.5 py-0.5 text-[10px] font-medium text-green-700">Current</span>
                    ) : (
                      <span className="rounded-full border border-slate-200 px-1.5 py-0.5 text-[10px] font-medium text-slate-400">Inactive</span>
                    )}
                  </span>
                  <span className="block text-xs text-slate-500">{formatAgreementDate(v.createDate)}</span>
                </button>
              </li>
            ))}
          </ul>
          <div className="min-w-0 flex-1 space-y-3">
            {selected ? (
              <>
                <div className="flex flex-wrap items-center gap-2">
                  <h3 className="text-base font-semibold text-slate-900">{selected.title}</h3>
                  <OutlineBadge>v{selected.version}</OutlineBadge>
                  {selected.membershipAgreementID === currentId ? (
                    <Badge variant="success" size="sm">
                      Active
                    </Badge>
                  ) : (
                    <Badge variant="neutral" size="sm">
                      Inactive
                    </Badge>
                  )}
                </div>
                <p className="text-xs text-slate-500">
                  Created: {formatAgreementDate(selected.createDate)}
                  {selected.membershipAgreementID !== currentId && <> · Ended: {formatAgreementDate(selected.endDate)}</>}
                </p>
                <div className="rounded-md border border-slate-200">
                  <p className="border-b border-slate-200 bg-slate-50 px-3 py-2 text-xs font-semibold text-slate-600">Agreement Content</p>
                  <div
                    className="prose prose-sm max-w-none px-4 py-3 text-slate-700"
                    data-testid="agreement-preview"
                    dangerouslySetInnerHTML={{ __html: selected.bodyHtml }}
                  />
                </div>
              </>
            ) : (
              <p className="py-8 text-center text-sm text-slate-500">Select a version to preview</p>
            )}
          </div>
        </div>
      )}
    </Modal>
  );
}
